import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@availity/element';

import { useAppContext } from '@/context';
import { checkNavigation, getCallback, JOURNEY_2FA_MANAGE } from '@/global';
import { useFRStep } from '@/hooks';
import {
  ErrorPage,
  LoginStart,
  TwoFAConfirmNumber,
  TwoFAEnrollmentSelectMethod,
  TwoFAHotpEnrollment,
  TwoFAChallengeSelectMethod,
  TwoFAHotpInputToken,
  TwoFAInputBackupCode,
  TwoFAManageBackupCodes,
  TwoFAManageHub,
  TwoFAAuthenticatorSetup,
  TwoFAAuthenticatorInputToken,
  TwoFAComplete,
  TwoFAWelcome,
} from '@/pages';

const forceAuth = true; // param will force the journey to rerun if needed

const TwoFAManage = () => {
  const { queryParams = {} } = useAppContext();
  const [currentStep, setCurrentStep] = useState();

  const { authIndexValue = '' } = queryParams;

  // process authIndexValue in event that the UI should navigate to a different journey
  const journeyId = useMemo(() => authIndexValue?.toLowerCase(), [authIndexValue]);

  // call utility function to check if we need to navigate to a different route
  useEffect(() => {
    if (journeyId) {
      checkNavigation(journeyId);
    }
  }, [journeyId]);

  // get FR step info from custom hook
  // NOTE: updating the currentStep 'should' trigger fetching of the stepToRender
  // currentStep should be changed on form submission
  const { stepToRender, errorCode } = useFRStep(currentStep, JOURNEY_2FA_MANAGE, forceAuth);

  const pageNodeToRender = useMemo(() => {
    let component;

    if (stepToRender) {
      const { payload = {} } = stepToRender;
      const { callbacks = [], stage } = payload;

      // check if password callback comes back in the step
      const hasPasswordCallback = !!getCallback(callbacks, 'passwordcallback');

      // check for login node first, else check for stage values
      if (hasPasswordCallback) {
        component = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
      } else {
        switch (stage) {
          case 'LoginpageUnique001': {
            component = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAManage-ShowMethods': {
            component = <TwoFAManageHub stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAManage-ShowCodes':
          case 'MFAManage-NewCodes': {
            component = <TwoFAManageBackupCodes stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-Welcome': {
            component = <TwoFAWelcome stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-SelectMethod': {
            component = <TwoFAEnrollmentSelectMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-OATHAppRegister': {
            component = <TwoFAAuthenticatorSetup stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-AppOATHVerify':
          case 'MFA-AppOATHVerify': {
            component = <TwoFAAuthenticatorInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-EnterSMSNumber': {
            component = (
              <TwoFAHotpEnrollment
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
                errorCode={errorCode}
                hotpType="sms"
              />
            );
            break;
          }
          case 'MFAEnrollment-EnterTTSNumber': {
            component = (
              <TwoFAHotpEnrollment
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
                errorCode={errorCode}
                hotpType="tts"
              />
            );
            break;
          }
          case 'MFA-SelectMethod': {
            component = <TwoFAChallengeSelectMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-ConfirmSMSNumber': {
            component = (
              <TwoFAConfirmNumber stepToRender={stepToRender} setCurrentStep={setCurrentStep} hotpType="sms" />
            );
            break;
          }
          case 'MFAEnrollment-ConfirmTTSNumber': {
            component = (
              <TwoFAConfirmNumber stepToRender={stepToRender} setCurrentStep={setCurrentStep} hotpType="tts" />
            );
            break;
          }
          case 'MFAEnrollment-SMSOTPVerify':
          case 'MFAEnrollment-TTSOTPVerify':
          case 'MFA-SMSOTPVerify':
          case 'MFA-TTSOTPVerify': {
            component = <TwoFAHotpInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFA-BackupVerify':
          case 'MFAEnrollment-BackupVerify': {
            component = <TwoFAInputBackupCode stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'MFAEnrollment-CompleteWithCodes':
          case 'MFA-CompleteWithCodes': {
            component = <TwoFAComplete stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes />;
            break;
          }
          case 'MFAEnrollment-CompleteWithoutCodes':
          case 'MFA-CompleteWithoutCodes': {
            component = (
              <TwoFAComplete stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes={false} />
            );
            break;
          }
          case 'ErrorPage': {
            component = <ErrorPage key="errorPage" />;
            break;
          }
          default: {
            component = <Box role="main">{stage}</Box>; // TODO: remove this after testing
          }
        }
      }
    }

    return component;
  }, [stepToRender]);

  return <Box role="main">{pageNodeToRender}</Box>;
};

export default TwoFAManage;
